/**
 * Stale-bundle detection (STALE-BUNDLE-M1).
 *
 * A tab opened before a deploy still runs the old index.html, which names
 * hashed chunks the new deploy no longer serves. The next lazy route then
 * fails to load — 34 times in the minutes after the 10-06 deploy, 12 of them
 * on /crew in Safari — and the user got "This screen didn't load".
 *
 * Both ways a failed chunk surfaces end here:
 *  - Vite's `vite:preloadError` window event, fired for every failed dynamic
 *    import (the chunk itself or a CSS dependency) before it rethrows;
 *  - RouteErrorBoundary, which catches the rethrown error from React.lazy.
 * One failure normally hits both, so the flag below flips once per page load
 * and StaleBundlePrompt renders a single prompt from it.
 *
 * Nothing here reloads the page. A crew member mid-form chooses when to
 * reload, and with no automatic reload there is no reload loop to guard.
 */

/**
 * Import-failure messages, per engine. ORCH's real event text can tighten
 * these; each must keep a matching case in staleBundle.test.ts.
 */
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /Unable to preload CSS for/i, // Vite's CSS preload helper
]

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message))
}

let stale = false
const listeners = new Set<() => void>()

/** Record that this page's bundle is out of date. Notifies only on the first call. */
export function markStaleBundle(): void {
  if (stale) return
  stale = true
  listeners.forEach((listener) => listener())
}

export function isStaleBundle(): boolean {
  return stale
}

export function subscribeStaleBundle(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * Listen for Vite's preload failures. Deliberately does NOT preventDefault:
 * Vite would then resolve the import with undefined, and the lazy wrapper's
 * `m.Page` would throw an unrelated TypeError instead of the chunk error.
 */
export function installPreloadErrorHandler(target: Window = window): () => void {
  const onPreloadError = () => markStaleBundle()
  target.addEventListener('vite:preloadError', onPreloadError)
  return () => target.removeEventListener('vite:preloadError', onPreloadError)
}

/** Tests only: a fresh page load. */
export function resetStaleBundleForTests(): void {
  stale = false
  listeners.clear()
}
